import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader, Tab, Message, Container, Icon, Grid } from 'semantic-ui-react';

import { useFetchChatSessionByIdQuery } from '../chat_sessions/chatSessionApiSlice';
import { useGetEvaluationSessionsQuery, useFetchEvaluationSessionByIdQuery } from '../evaluation_sessions/evaluationSessionApiSlice';
import { evaluatedOutcome } from '../evaluation_sessions/evaluationRules';
import { useGetProductKnowledgeEvaluationsQuery } from '../product_knowledge/productKnowledgeApiSlice';
import { formatDate } from '../../constants';
import { useBreakpoint } from '../../hooks/useBreakpoint';

import ChatSessionDetail from '../chat_sessions/ChatSessionDetail';
import EvaluatedCriteria from '../evaluation_sessions/EvaluatedCriteria';
import EvaluationSummary from '../evaluation_sessions/EvaluationSummary';
import { SHOW_CHATLOG_EVENT, SHOW_CRITERION_EVENT, scrollToWhenReady } from '../evaluation_sessions/evaluationRules';
import ProductKnowledgeCriteriaList, { pkScoreDisplay } from '../product_knowledge/ProductKnowledgeCriteriaList';
import ProductKnowledgeSummary from '../product_knowledge/ProductKnowledgeSummary';
import { customerLabel, minutesLabel, shortDate } from './MobileChatSessionList';
import { JumpBackPill, SectionTitle, StatTile, scoreColor } from '../../components/reportCards';
import { SkillScoreLabel, TipLabel, scoreText } from '../../components/listPage';
import { RESTORE_EVENT, installJumpBack, restoreScroll, setReportTab } from '../evaluation_sessions/reportJump';

const SHOW_SALES_LIKELIHOOD = false; // ACE feedback CSV 16

const ChatSessionReport = () => {
    const { isDesktop, isMobile } = useBreakpoint();
    const isMobileOrTablet = !isDesktop;
    const { id: chatSessionId } = useParams();

    // 1. Fetch Chat Session
    const { 
        data: chatSession, 
        isLoading: isChatLoading, 
        isError: isChatError 
    } = useFetchChatSessionByIdQuery(chatSessionId);

    // 2. Fetch Soft Skills Evaluations List (EvaluationSessions)
    // We filter by chat_session ID and potentially take the latest one.
    // Poll while an evaluation exists but is still being processed: trainees land here straight
    // from 종료하고 평가 받기, so the common case is "created seconds ago, criteria still filling
    // in". Sessions with no evaluation at all must NOT poll — nothing will ever arrive for them.
    // (The 대화 내용 pane already polls independently via ChatSessionDetail.)
    const [evalPolling, setEvalPolling] = React.useState(0);
    // Phone report tabs: 0 응대 스킬, 1 제품 설명, 2 대화 내용 (see mobileTabs).
    const [mobileTab, setMobileTab] = React.useState(0);
    React.useEffect(() => {
        if (!isMobileOrTablet) return undefined;
        const show = (e) => {
            setMobileTab(2);
            scrollToWhenReady(e.detail);
        };
        window.addEventListener(SHOW_CHATLOG_EVENT, show);
        return () => window.removeEventListener(SHOW_CHATLOG_EVENT, show);
    }, [isMobileOrTablet]);
    // Desktop middle pane: 0 응대 스킬, 1 제품 설명. A 보완점 / 평가 기준 link to a criterion card
    // switches to 응대 스킬 first — the card isn't rendered while 제품 설명 is showing.
    const [paneTab, setPaneTab] = React.useState(0);
    React.useEffect(() => {
        if (isMobileOrTablet) return undefined;
        const show = (e) => {
            setPaneTab(0);
            scrollToWhenReady(`criterion_int_id_${e.detail}`);
        };
        window.addEventListener(SHOW_CRITERION_EVENT, show);
        return () => window.removeEventListener(SHOW_CRITERION_EVENT, show);
    }, [isMobileOrTablet]);

    // Jumps between linked items come with a way back (reportJump.js): record the tab with
    // each jump, and on 돌아가기 / the back gesture put the tab, then the scroll, back.
    React.useEffect(() => installJumpBack(), []);
    React.useEffect(() => setReportTab(isMobileOrTablet ? mobileTab : paneTab), [isMobileOrTablet, mobileTab, paneTab]);
    React.useEffect(() => {
        const restore = (e) => {
            const entry = e.detail;
            if (entry.tab != null) (isMobileOrTablet ? setMobileTab : setPaneTab)(entry.tab);
            // After the tab is shown and laid out.
            requestAnimationFrame(() => requestAnimationFrame(() => restoreScroll(entry)));
        };
        window.addEventListener(RESTORE_EVENT, restore);
        return () => window.removeEventListener(RESTORE_EVENT, restore);
    }, [isMobileOrTablet]);

    const {
        data: evalSessionsData,
        isLoading: isEvalListLoading
    } = useGetEvaluationSessionsQuery(
        { chat_session: chatSessionId },
        { pollingInterval: evalPolling },
    );

    const evalSessions = evalSessionsData?.results || evalSessionsData || [];
    // The list endpoint orders by (is_postprocessed, -created_at), so results[0]
    // is not necessarily the newest session (an older, unprocessed one sorts first).
    // Pick the latest by created_at explicitly.
    const latestEvalSessionBrief = evalSessions.length > 0
        ? [...evalSessions].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
        : null;
    const latestEvalSessionId = latestEvalSessionBrief?.id;

    // 2b. Fetch Detailed Soft Skills Evaluation
    // The list view only returns a "Brief" serializer which lacks the detailed 'evaluatedcriterion_set.result'
    // needed for the UI. So we must fetch the detail by ID.
    const {
        data: detailedEvalSession,
        isLoading: isEvalDetailLoading
    } = useFetchEvaluationSessionByIdQuery(latestEvalSessionId, {
        skip: !latestEvalSessionId,
        pollingInterval: evalPolling,
    });

    // Poll only for an evaluation that exists and hasn't finished postprocessing.
    React.useEffect(() => {
        const pending = !!latestEvalSessionId && !detailedEvalSession?.is_postprocessed;
        setEvalPolling(pending ? 5000 : 0);
    }, [latestEvalSessionId, detailedEvalSession]);

    // 3. Fetch Product Knowledge Evaluations
    const { 
        data: pkEvalsData, 
        isLoading: isPkLoading 
    } = useGetProductKnowledgeEvaluationsQuery({ chat_session_id: chatSessionId });

    // Calculate effective loading state
    // If we found an ID, we must wait for its detail loading
    const isEvalLoading = isEvalListLoading || (latestEvalSessionId && isEvalDetailLoading);
    const isLoading = isChatLoading || isEvalLoading || isPkLoading;

    if (isLoading) return <Loader active>결과를 불러오는 중...</Loader>;
    if (isChatError || !chatSession) return <Message error>롤플레이를 불러오지 못했습니다.</Message>;

    // Get the latest Soft Skills Evaluation
    // Use the detailed one if available, otherwise fall back (though brief is likely insufficient)
    const latestEvalSession = detailedEvalSession || latestEvalSessionBrief;

    // Get the latest Product Knowledge Evaluation
    const pkEvals = pkEvalsData?.results || pkEvalsData || [];
    const latestPkEval = pkEvals.length > 0 ? pkEvals[0] : null;

    // Middle Pane Tabs
    const criteriaPanes = [
        {
            menuItem: '응대 스킬',
            render: () => (
                 <Tab.Pane style={innerTabPaneStyle} data-report-scroll="criteria">
                    {latestEvalSession?.evaluatedcriterion_set?.length ? (
                        <EvaluatedCriteria evaluatedcriterion_set={latestEvalSession.evaluatedcriterion_set} />
                    ) : latestEvalSession ? (
                        <Message icon>
                            <Icon name="circle notched" loading />
                            <Message.Content>
                                <Message.Header>평가 처리 중입니다</Message.Header>
                                완료되면 이 화면에 자동으로 표시됩니다.
                            </Message.Content>
                        </Message>
                    ) : <Message info>응대 스킬 데이터 없음.</Message>}
                 </Tab.Pane>
            )
        },
        {
            menuItem: '제품 설명',
            render: () => (
                <Tab.Pane style={innerTabPaneStyle} data-report-scroll="claims">
                    <ProductKnowledgeCriteriaList pkEvaluation={latestPkEval} />
                </Tab.Pane>
            )
        }
    ];

    // Phones and tablets: one pane at a time, the result first — the transcript (often 10+
    // screens of bubbles) used to be what a trainee landed on. 관련 말풍선 links switch to the
    // transcript and scroll to the bubble (SHOW_CHATLOG_EVENT above).
    const mobileTabs = ['응대 스킬', '제품 설명', '대화 내용'];
    const changeMobileTab = (i) => {
        setMobileTab(i);
        window.scrollTo(0, 0);
    };

    // Phones/tablets: the page grey of the other trainee pages, so the white cards read as cards.
    return (
        <div style={{ height: 'auto', display: 'flex', flexDirection: 'column', ...(isMobileOrTablet ? { backgroundColor: '#f9fafb', minHeight: 'calc(100dvh - 50px)' } : {}) }}>
            {/* Header Section */}
            {/* Phones/tablets: the shared header gap and 14px gutter (docs/FRONTEND_UI.md), a
                back chip like 내 피드백's detail, and the session itself as the subtitle — the
                line its 롤플레이 기록 card showed. */}
            <div className={isMobileOrTablet ? 'page-header-band' : undefined} style={{
                flex: '0 0 auto',
                padding: '16px 0',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                borderBottom: '1px solid rgba(0,0,0,0.05)',
            }}>
                <Container fluid>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: isMobile ? 0 : '0 20px' }}>
                        {isMobileOrTablet ? (
                            <Link to="/chatsessionslist" title="롤플레이 기록으로" style={headerChipStyle}>
                                <Icon name="arrow left" className="centered-icon" style={{ color: '#007AFF', fontSize: '17px' }} />
                            </Link>
                        ) : (
                            <div style={headerChipStyle}>
                                <Icon name="file alternate outline" style={{ margin: 0, color: '#007AFF', fontSize: '18px' }} />
                            </div>
                        )}
                        <div style={{ minWidth: 0 }}>
                            <h2 style={{ fontSize: '17px', fontWeight: '600', margin: 0, color: '#1d1d1f' }}>
                                롤플레이 결과
                            </h2>
                            <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>
                                {isMobileOrTablet && chatSession
                                    ? [customerLabel(chatSession.persona), shortDate(chatSession.created_at), minutesLabel(chatSession.duration), chatSession.is_practice ? '연습' : null].filter(Boolean).join(' · ')
                                    : '대화 내용, 응대 스킬 및 제품 설명'}
                            </p>
                        </div>
                    </div>
                </Container>
            </div>

            <Container fluid style={{ padding: isMobileOrTablet ? (isMobile ? '0 0 40px' : '0 20px 40px') : '20px', flex: 1 }}>
                {isMobileOrTablet ? (
                    <>
                        <ScoreTiles evalSession={latestEvalSession} pkEval={latestPkEval} />

                        {/* Pills, as 롤플레이 기록's filters, pinned under the navbar: switching on a
                            long report shouldn't mean scrolling back up. */}
                        <div style={{ position: 'sticky', top: '50px', zIndex: 20, display: 'flex', gap: '8px', padding: '10px 0', margin: '4px 0 6px', backgroundColor: 'rgba(249, 250, 251, 0.95)', backdropFilter: 'blur(12px)' }}>
                            {mobileTabs.map((label, i) => (
                                <button
                                    key={label}
                                    type="button"
                                    onClick={() => changeMobileTab(i)}
                                    style={{ flex: 1, border: 'none', borderRadius: '999px', padding: '8px 0', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', backgroundColor: mobileTab === i ? '#1d1d1f' : '#F2F2F7', color: mobileTab === i ? 'white' : '#424245' }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        {/* No white panel around a tab: the cards inside sit on the gutter. */}
                        {/* All three panes stay mounted (inactive ones hidden), so a tab you jump away
                            from keeps its open cards and comes back exactly as it was. */}
                        <div style={{ display: mobileTab === 0 ? 'block' : 'none' }}>
                        {latestEvalSession ? (
                            <>
                                <SectionTitle first>응대 스킬 요약</SectionTitle>
                                <div style={mobileCardStyle}>
                                    <EvaluationSummary evaluationsession={latestEvalSession} hideHeader />
                                </div>
                                <SectionTitle meta={criteriaMeta(latestEvalSession)}>평가 항목</SectionTitle>
                                <EvaluatedCriteria evaluatedcriterion_set={latestEvalSession.evaluatedcriterion_set} filterable />
                            </>
                        ) : <EmptyNote text="응대 스킬 평가가 없습니다." />}
                        </div>
                        <div style={{ display: mobileTab === 1 ? 'block' : 'none' }}>
                        {latestPkEval ? (
                            <>
                                {/* Same shape as the 응대 스킬 tab: section header, summary card, then the items. */}
                                {latestPkEval.result?.summary && (
                                    <>
                                        <SectionTitle first>평가 요약</SectionTitle>
                                        <div style={{ ...mobileCardStyle, fontSize: '14px', lineHeight: 1.6, color: '#424245' }}>
                                            {latestPkEval.result.summary}
                                        </div>
                                    </>
                                )}
                                <ProductKnowledgeCriteriaList pkEvaluation={latestPkEval} first={!latestPkEval.result?.summary} />
                            </>
                        ) : <EmptyNote text="제품 설명 평가가 없습니다." />}
                        </div>
                        <div style={{ display: mobileTab === 2 ? 'block' : 'none', ...mobileCardStyle, padding: '12px' }}>
                            <ChatSessionDetail chat_session_id={chatSessionId} evaluatedCriteria={latestEvalSession?.evaluatedcriterion_set} />
                        </div>
                    </>
                ) : (
                    <Grid columns={3} stackable>
                        {/* 1. Chat Transcript Pane */}
                        <Grid.Column width={5}>
                             <div className="custom-card" style={{ height: 'calc(100vh - 170px)', padding: '0' }}>
                                <div className="custom-card-header" style={{ paddingTop: '20px', paddingLeft: '20px' }}>
                                    <Icon name="comments outline" style={{ color: '#86868b' }} />
                                    <h3 className="custom-card-title" style={{ marginLeft: '8px' }}>대화 내용</h3>
                                </div>
                                <div data-report-scroll="transcript" style={{ flex: 1, overflowY: 'auto', padding: '0 20px' }}>
                                     <ChatSessionDetail chat_session_id={chatSessionId} evaluatedCriteria={latestEvalSession?.evaluatedcriterion_set} />
                                </div>
                             </div>
                        </Grid.Column>

                        {/* 2. Middle Pane (Criteria Tabs) */}
                        <Grid.Column width={6}>
                            <div className="custom-card" style={{ height: 'calc(100vh - 170px)', padding: '0', display: 'flex', flexDirection: 'column' }}>
                                <div style={{ flex: '0 0 auto', padding: '10px 20px 0' }}>
                                     {/* We render the Tab menu here manually or just render the Tab component which includes the menu */}
                                     {/* Using Tab component directly inside the card */}
                                </div>
                                <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                     <Tab 
                                        menu={{ secondary: true, pointing: true, style: { padding: '0 10px' } }}
                                        panes={criteriaPanes}
                                        activeIndex={paneTab}
                                        onTabChange={(e, { activeIndex }) => setPaneTab(activeIndex)}
                                        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                        renderActiveOnly={true}
                                     />
                                </div>
                            </div>
                        </Grid.Column>

                        {/* 3. Right Pane (Summaries) */}
                        <Grid.Column width={5}>
                             <div className="custom-card" style={{ height: 'calc(100vh - 170px)', padding: '0' }}>
                                <div className="custom-card-header" style={{ paddingTop: '20px', paddingLeft: '20px' }}>
                                    <Icon name="file alternate" style={{ color: '#86868b' }} />
                                    <h3 className="custom-card-title" style={{ marginLeft: '8px' }}>요약</h3>
                                </div>
                                <div data-report-scroll="summary" style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                                    
                                    {/* User Info Header */}
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '12px', 
                                        marginBottom: '2rem',
                                        paddingBottom: '1.5rem',
                                        borderBottom: '1px solid #f0f0f0' 
                                    }}>
                                        <div style={{ 
                                            width: '48px', 
                                            height: '48px', 
                                            borderRadius: '50%', 
                                            backgroundColor: '#F5F5F7', 
                                            color: '#1d1d1f', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            fontSize: '20px', 
                                            fontWeight: '600' 
                                        }}>
                                            {chatSession.trainee.full_name ? chatSession.trainee.full_name[0] : 'U'}
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '16px', fontWeight: '600', color: '#1d1d1f' }}>
                                                {chatSession.trainee.full_name || chatSession.trainee.username}
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#86868b', marginTop: '2px' }}>
                                                {formatDate(chatSession.created_at)}
                                                <span style={{ margin: '0 6px', color: '#d2d2d7' }}>|</span>
                                                <Icon name="clock outline" style={{ marginRight: '4px' }} />
                                                {chatSession.duration ? chatSession.duration.split('.')[0] : '-'}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Scores: the same tiles as the phone report. */}
                                    <div style={{ marginBottom: '2rem' }}>
                                        <ScoreTiles evalSession={latestEvalSession} pkEval={latestPkEval} />
                                    </div>

                                    {SHOW_SALES_LIKELIHOOD && latestEvalSession?.summary?.sales_likelihood_percentage !== undefined && (
                                        <div style={{ 
                                            marginBottom: '2rem', 
                                            padding: '1rem', 
                                            backgroundColor: '#f8f9fa', 
                                            borderRadius: '12px',
                                            textAlign: 'center'
                                        }}>
                                            <div style={{ fontSize: '0.85rem', color: '#86868b', marginBottom: '0.5rem', fontWeight: 600 }}>
                                                판매 성공 예측
                                            </div>
                                            <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: '2px', justifyContent: 'center' }}>
                                                <span style={{ 
                                                    fontSize: '1.8rem', 
                                                    fontWeight: '700', 
                                                    lineHeight: 1.1,
                                                    color: latestEvalSession.summary.sales_likelihood_percentage >= 70 ? '#34C759' : 
                                                           latestEvalSession.summary.sales_likelihood_percentage >= 40 ? '#FF9500' : '#FF3B30'
                                                }}>
                                                    {latestEvalSession.summary.sales_likelihood_percentage}
                                                </span>
                                                <span style={{ fontSize: '1rem', color: '#86868b', fontWeight: '600' }}>%</span>
                                            </div>
                                            {latestEvalSession.summary.sales_likelihood_explanation && (
                                                <div style={{ 
                                                    fontSize: '13px', 
                                                    color: '#1d1d1f', 
                                                    marginTop: '12px',
                                                    lineHeight: '1.5',
                                                    textAlign: 'left'
                                                }}>
                                                    {latestEvalSession.summary.sales_likelihood_explanation}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Soft Skills Summary */}
                                    {latestEvalSession && (
                                        <div style={{ marginBottom: '2rem' }}>
                                            <h4 style={{ marginBottom: '1rem', color: '#666' }}>응대 스킬</h4>
                                            <EvaluationSummary evaluationsession={latestEvalSession} hideHeader={true} />
                                        </div>
                                    )}
                                    
                                    {/* Product Knowledge Summary */}
                                    {latestPkEval && (
                                        <div>
                                            <h4 style={{ marginBottom: '1rem', color: '#666' }}>제품 설명</h4>
                                            <ProductKnowledgeSummary pkEvaluation={latestPkEval} />
                                        </div>
                                    )}
                                </div>
                             </div>
                        </Grid.Column>
                    </Grid>
                )}
            </Container>
            <JumpBackPill />
        </div>
    );
};

const headerChipStyle = {
    width: '40px', height: '40px', flexShrink: 0, borderRadius: '50%', backgroundColor: '#F2F2F7',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const mobileCardStyle = {
    backgroundColor: 'white', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)',
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)', padding: '16px', marginBottom: '12px',
};

const criteriaMeta = (evalSession) => {
    const criteria = evalSession?.evaluatedcriterion_set || [];
    if (!criteria.length) return null;
    return `${criteria.length}개 중 ${criteria.filter((c) => evaluatedOutcome(c)?.favorable).length}개 충족`;
};


/** Phones: the two scores first, as on the 롤플레이 기록 card, with what each was out of. */
const ScoreTiles = ({ evalSession, pkEval }) => {
    const criteria = evalSession?.evaluatedcriterion_set || [];
    const met = criteria.filter((c) => evaluatedOutcome(c)?.favorable).length;
    // 응대 스킬 점수 on 100, as the lists show it; raw points were coloured against 80/50 as if
    // they were already out of 100. Maximum as the backend's get_score_possible (no deduction rows).
    const max = criteria.reduce((sum, c) => sum + Math.max(0, c.criterion.score), 0);
    const pct = max > 0 && evalSession?.total_score != null ? Math.max(0, evalSession.total_score * 100 / max) : null;
    const sales = pct == null ? null : { value: scoreText(pct), score: pct, sub: `${evalSession.total_score} / ${max}점 · 충족 ${met} / ${criteria.length}` };
    const tiles = [[<SkillScoreLabel />, sales, 'skill'], [<TipLabel hint="pk">설명 정확도</TipLabel>, pkScoreDisplay(pkEval), 'pk']];
    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {tiles.map(([label, d, key]) => (
                <StatTile
                    key={key}
                    label={label}
                    value={d ? d.value : '—'}
                    color={d ? scoreColor(d.score) : '#c7c7cc'}
                    sub={d ? d.sub : '평가 없음'}
                />
            ))}
        </div>
    );
};

const EmptyNote = ({ text }) => (
    <div style={{ ...mobileCardStyle, textAlign: 'center', color: '#86868b', fontSize: '14px', padding: '32px 16px' }}>{text}</div>
);

const innerTabPaneStyle = {
    height: '100%',
    overflowY: 'auto',
    border: 'none',
    boxShadow: 'none',
    padding: '20px'
};

export default ChatSessionReport;
